import { SearchServiceBase, SearchApi } from 'src/services/search.service';
import { Injectable, inject } from '@angular/core';
import {
  SpinnerV2Service,
  StorageV2Service,
  GridUtilsV2,
  AssetService,
} from '@schenck/rotec-components';
import { ToolingHistorySettingsService } from './tooling-history-settings.service';
import { ToolingHistory } from './tooling-history.model';
import { ToolingTaskService } from 'src/services';
import { switchMap, map } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';
import { gridSchedulingMultiselectFilter } from 'src/components/task-interval.pipe';

@Injectable()
export class ToolingHistorySearchApi implements SearchApi<ToolingHistory> {
  private readonly taskService = inject(ToolingTaskService);
  private readonly assetService = inject(AssetService);

  reload$ = this.taskService.taskChange$;

  search$(date: { from: Date; to: Date } | null) {
    return this.taskService.search$(false, date).pipe(
      switchMap((results) => {
        if (results.length === 0) {
          // necessary to handle this because combineLatest won't emit anything if it gets passed an empty array
          return of([]);
        }

        const searchResults$ = results.map((result) => {
          const tooling$ = this.assetService.tooling$(result.toolingId);
          const machines$ = this.assetService.machinesThatHaveTooling$(tooling$);

          return combineLatest([tooling$, machines$]).pipe(
            map(([tooling, machines]): ToolingHistory => {
              if (!tooling) {
                return {
                  ...result,
                  machines: '',
                  toolingName: '',
                };
              }
              return {
                ...result,
                machines: machines
                  .map((machine) => machine.name + ' (' + machine.serial + ')')
                  .join(', '),
                toolingName: tooling.name,
                serial: tooling.serial,
                inventoryNo: tooling.inventoryNo,
                mainContact: tooling.mainContact,
                manufacturer: tooling.manufacturer,
                verificationLevel: tooling.verificationLevel,
                schenckMatNo: tooling.schenckMatNo,
                storageLocation: tooling.storageLocation,
                workplace: tooling.workplace,
              };
            })
          );
        });

        return combineLatest(searchResults$);
      })
    );
  }
}

@Injectable()
export class ToolingHistorySearchService extends SearchServiceBase<ToolingHistory> {
  constructor() {
    const searchAPi = inject(ToolingHistorySearchApi);
    const loadingService = inject(SpinnerV2Service);
    const settingsService = inject(ToolingHistorySettingsService);
    const storageService = inject(StorageV2Service);

    super(searchAPi, loadingService, settingsService, storageService, {
      key: 'ToolingHistorySearch',
      primaryDateProp: 'executionDate',
      filter: {
        toolingName: GridUtilsV2.multiselectFilter,
        manufacturer: GridUtilsV2.multiselectFilter,
        inventoryNo: GridUtilsV2.textFilter,
        serial: GridUtilsV2.textFilter,
        schenckMatNo: GridUtilsV2.textFilter,
        verificationLevel: GridUtilsV2.multiselectFilter,
        customerId: GridUtilsV2.textFilter,
        schenckId: GridUtilsV2.textFilter,
        title: GridUtilsV2.textFilter,
        priority: GridUtilsV2.checkboxFilter,
        responsibleCompany: GridUtilsV2.radioFilter,
        responsible: GridUtilsV2.textFilter,
        executive: GridUtilsV2.textFilter,
        dueDate: GridUtilsV2.dateRangeFilter,
        executionDate: GridUtilsV2.dateRangeFilter,
        description: GridUtilsV2.textFilter,
        type: GridUtilsV2.multiselectFilter,
        scheduling: gridSchedulingMultiselectFilter,
        machines: GridUtilsV2.textFilter, // TODO multiselect?
        toolingForRotor: GridUtilsV2.textFilter,
        mainContact: GridUtilsV2.textFilter,
        storageLocation: GridUtilsV2.textFilter,
        workplace: GridUtilsV2.textFilter,
      },
    });
  }
}
